import type {
  RepositoryPickerRepository$data as Repository,
  RepositoryPickerRepository$key,
} from '@github-ui/item-picker/RepositoryPickerRepository.graphql'
import {useState, useCallback, useId, useEffect, Suspense} from 'react'
import {graphql, readInlineData, usePreloadedQuery, useQueryLoader, type PreloadedQuery} from 'react-relay'
import {Dialog, Button, Link} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon, CommentIcon, FileCodeIcon} from '@primer/octicons-react'
import {RepositoryFragment, RepositoryPicker, TopRepositories} from '@github-ui/item-picker/RepositoryPicker'
import styles from './CopilotRefineIssueDialog.module.css'
import type {RepositoryPickerTopRepositoriesQuery} from '@github-ui/item-picker/RepositoryPickerTopRepositoriesQuery.graphql'
import {useSearch} from './use-search'
import type {CopilotRefineIssueDialogQuery} from './__generated__/CopilotRefineIssueDialogQuery.graphql'

export interface CopilotRefineIssueDialogProps {
  title?: string
  markdown: string
  copilotApiUrl: string
  owner?: string
  repoName?: string
  onClose: () => void
}

const DEFAULT_TITLE = 'Issue title is not set'

export const CopilotRefineIssueDialogGraphQLQuery = graphql`
  query CopilotRefineIssueDialogQuery($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      ...RepositoryPickerRepository
    }
  }
`

export function CopilotRefineIssueDialog(props: CopilotRefineIssueDialogProps) {
  const [topReposQueryRef, loadTopRepos, disposeTopRepos] =
    useQueryLoader<RepositoryPickerTopRepositoriesQuery>(TopRepositories)

  const [currentRepoQueryRef, loadCurrentRepo, disposeCurrentRepo] = useQueryLoader<CopilotRefineIssueDialogQuery>(
    CopilotRefineIssueDialogGraphQLQuery,
  )

  useEffect(() => {
    loadTopRepos({topRepositoriesFirst: 5, hasIssuesEnabled: true, owner: null}, {fetchPolicy: 'store-or-network'})
    return () => {
      disposeTopRepos()
    }
  }, [disposeTopRepos, loadTopRepos])

  useEffect(() => {
    if (props.owner && props.repoName) {
      loadCurrentRepo({owner: props.owner, name: props.repoName}, {fetchPolicy: 'store-or-network'})
    }

    return () => disposeCurrentRepo()
  }, [disposeCurrentRepo, loadCurrentRepo, props.owner, props.repoName])

  if (!topReposQueryRef || !currentRepoQueryRef) {
    return null // Return nothing while loading instead of showing a partial dialog
  }

  return (
    <Suspense>
      <CopilotRefineIssueDialogInternal
        {...props}
        topReposQueryRef={topReposQueryRef}
        currentRepoQueryRef={currentRepoQueryRef}
      />
    </Suspense>
  )
}

interface CopilotRefineIssueDialogInternalProps extends CopilotRefineIssueDialogProps {
  topReposQueryRef: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
  currentRepoQueryRef: PreloadedQuery<CopilotRefineIssueDialogQuery>
}

function CopilotRefineIssueDialogInternal({
  title = DEFAULT_TITLE,
  markdown,
  copilotApiUrl,
  onClose,
  topReposQueryRef,
  currentRepoQueryRef,
}: CopilotRefineIssueDialogInternalProps) {
  const [expandedSnippets, setExpandedSnippets] = useState<Record<string, boolean>>({})
  const [hasRunSearch, setHasRunSearch] = useState(false)

  const currentRepoData = usePreloadedQuery<CopilotRefineIssueDialogQuery>(
    CopilotRefineIssueDialogGraphQLQuery,
    currentRepoQueryRef,
  )

  const repository =
    currentRepoData && currentRepoData.repository !== undefined
      ? // eslint-disable-next-line no-restricted-syntax
        readInlineData<RepositoryPickerRepository$key>(RepositoryFragment, currentRepoData.repository) || undefined
      : null

  const [selectedRepository, setSelectedRepository] = useState<Repository | undefined>(repository ?? undefined)

  const {isSearching, searchError, search, suggestedSnippets} = useSearch(title, markdown, copilotApiUrl)

  const toggleSnippet = useCallback((snippetId: string) => {
    setExpandedSnippets(current => ({
      ...current,
      [snippetId]: !current[snippetId],
    }))
  }, [])

  const isLoading = isSearching

  const handleSearch = useCallback(async () => {
    if (!isLoading && selectedRepository) {
      await search({
        repository: selectedRepository,
      })
      setHasRunSearch(true)
    }
  }, [isLoading, search, selectedRepository])

  const pluralize = (count: number) => {
    if (count === 1) {
      return 'snippet'
    }
    if (count % 10 === 1 && count % 100 !== 11) {
      return 'snippet'
    }
    return 'snippets'
  }

  const descriptionId = useId()

  return (
    <Dialog
      title="Refine issue"
      onClose={onClose}
      width="xlarge"
      renderBody={() => (
        <div className={styles.dialogBody}>
          <div className={styles.dialogHeader}>
            <RepositoryPicker
              aria-describedby={descriptionId}
              initialRepository={selectedRepository}
              onSelect={setSelectedRepository}
              organization={repository?.owner.login}
              topReposQueryRef={topReposQueryRef}
              focusRepositoryPicker
              enforceAtleastOneSelected
              options={{hasIssuesEnabled: true}}
            />
          </div>
          <div className={styles.stepContent}>
            <div className={styles.searchControls}>
              <div className={styles.selectContainer}>
                {hasRunSearch && suggestedSnippets.length > 0 && (
                  <span>
                    Found {suggestedSnippets.length} {pluralize(suggestedSnippets.length)}
                  </span>
                )}
                <Button onClick={handleSearch} loading={isSearching} disabled={isLoading}>
                  Send to CAPI search
                </Button>
              </div>
            </div>
            {searchError && <div className="color-fg-danger">{searchError}</div>}
          </div>

          {suggestedSnippets.length > 0 && (
            <div className={styles.stepContent}>
              <label htmlFor="snippets">Suggested snippets</label>
              <div id="snippets">
                {suggestedSnippets.map(snippet => (
                  <div key={snippet.id} className={styles.snippet}>
                    <div className={styles.snippetHeader}>
                      <Button
                        variant="invisible"
                        className={styles.stepButton}
                        onClick={() => toggleSnippet(snippet.id)}
                      >
                        {expandedSnippets[snippet.id] ? <ChevronDownIcon /> : <ChevronRightIcon />}
                        {snippet.type === 'github.text' ? (
                          <>
                            <CommentIcon />
                            <span>text</span>
                          </>
                        ) : (
                          <>
                            <FileCodeIcon />
                            <span>{snippet.metadata.display_name}</span>
                          </>
                        )}
                      </Button>
                    </div>
                    {expandedSnippets[snippet.id] && (
                      <div className={styles.snippetContent}>
                        {snippet.data.url && <Link href={snippet.data.url}>View source</Link>}
                        {snippet.type === 'github.snippet' && <pre>{snippet.data.contents}</pre>}
                        {snippet.type === 'github.text' && <span>{snippet.data.text}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    />
  )
}

try{ CopilotRefineIssueDialog.displayName ||= 'CopilotRefineIssueDialog' } catch {}
try{ CopilotRefineIssueDialogInternal.displayName ||= 'CopilotRefineIssueDialogInternal' } catch {}